"use client";

import Script from "next/script";
import { useEffect } from "react";

import { useConsent } from "@/lib/consent-context";
import { useIsEmbedded } from "@/lib/embedded";
import { gtmSetRegion } from "@/lib/gtm";
import { usePublicPathname } from "@/lib/public-pathname";

const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;
const HUBSPOT_PORTAL_ID = process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID;
const HUBSPOT_ENABLED = process.env.NEXT_PUBLIC_HUBSPOT_ENABLED === "true";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    _hsq?: Array<[string, ...unknown[]]>;
    fbq?: (...args: unknown[]) => void;
  }
}

type ConsentChoice = {
  statistics: boolean;
  marketing: boolean;
  preferences: boolean;
};

function consentSignals(consent: ConsentChoice) {
  return {
    ad_storage: consent.marketing ? "granted" : "denied",
    ad_user_data: consent.marketing ? "granted" : "denied",
    ad_personalization: consent.marketing ? "granted" : "denied",
    analytics_storage: consent.statistics ? "granted" : "denied",
    functionality_storage: consent.preferences ? "granted" : "denied",
    personalization_storage: consent.preferences ? "granted" : "denied",
  };
}

function pushConsentToDataLayer(consent: ConsentChoice) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("consent", "update", consentSignals(consent));
}

export function ConsentScripts() {
  const { consent, hasDecided } = useConsent();
  const pathname = usePublicPathname();
  const embedded = useIsEmbedded();

  useEffect(() => {
    if (!hasDecided || embedded) return;
    pushConsentToDataLayer({
      statistics: consent.statistics,
      marketing: consent.marketing,
      preferences: consent.preferences,
    });
  }, [consent, hasDecided, embedded]);

  useEffect(() => {
    if (embedded) return;
    gtmSetRegion();
  }, [pathname, embedded]);

  useEffect(() => {
    if (window._hsq && consent.marketing && !embedded) {
      window._hsq.push(["setPath", pathname]);
      window._hsq.push(["trackPageView"]);
    }
  }, [pathname, consent.marketing, embedded]);

  return (
    <>
      {!embedded && GTM_ID && hasDecided && (consent.statistics || consent.marketing) && (
        <Script id="gtm-loader" strategy="afterInteractive">{`
          window.gtag && window.gtag('consent', 'update', ${JSON.stringify(consentSignals(consent))});
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTM_ID}');
        `}</Script>
      )}

      {!embedded && HUBSPOT_PORTAL_ID && HUBSPOT_ENABLED && consent.marketing && (
        <Script
          id="hs-script-loader"
          strategy="afterInteractive"
          src={`//js.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`}
        />
      )}
    </>
  );
}
